import {Component, inject, Input} from '@angular/core';
import {InvoicesService} from '@modules/universal-billing/billing/invoice/invoices.service';
import {Observable} from 'rxjs';
import {PageResult} from '@shared/components/classes/page-result';
import {BasicTableComponent} from '@common/references/basic-table.component';
import {SharedModule} from '@primeng/api';
import {Table} from '@primeng/table';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {InvoiceCardComponent} from '@modules/universal-billing/billing/invoice/invoice-card/invoice-card.component';
import {Invoice} from '@modules/universal-billing/billing/invoice/invoices.interfaces';
import {PaymentsFormComponent} from '@modules/universal-billing/accounting/payments/payments-form/payments-form.component';
import {ModalClose} from '@shared/services/modal/modal.enum';
import {InvoicePaymentStatus, invoicePaymentStatusMapper} from '@shared/enums/InvoicePayment.enum';
import {PTableCellValueComponent} from '@primeng/table/components/p-table-cell-value/p-table-cell-value.component';
import {ReferenceField} from '@common/references/reference-field';
import {HttpClient} from '@angular/common/http';
import urlJoin from 'url-join';
import { DatePipe } from '@angular/common';
import { environment } from '@environments/environment';
import {InvoiceStatus, invoiceStatusMapper} from '@shared/enums/InvoiceStatus.enum';
import { Renters } from '@modules/universal-billing/renters/renters.interfaces';
import { InvoicePaymentStatusDirective } from '@common/directive/invoice-payment-status.directive';
import { InvoiceStatusDirective } from '@common/directive/invoice-status.directive';
import {InvoiceModalService} from '@shared/components/confirm-invoice-type/invoice-modal.service';

@Component({
  selector: 'apartment-invoices',
  standalone: true,
  imports: [
    SharedModule,
    Table,
    TranslatePipe,
    PTableCellValueComponent,
    DatePipe,
    InvoicePaymentStatusDirective,
    InvoiceStatusDirective
  ],
  templateUrl: './apartment-invoices.component.html',
  styleUrl: './apartment-invoices.component.scss'
})
export class ApartmentInvoicesComponent extends BasicTableComponent<any> {
  @Input() apartmentId: number;
  @Input() period: Date;
  @Input() renter: Renters;
  service = inject(InvoicesService);
  invoiceModalService = inject(InvoiceModalService);
  addQueryToUrl = false;
  fields: ReferenceField[] = [];
  _http = inject(HttpClient);

  paymentStatusBadgeColors = {
    [InvoicePaymentStatus.PAID]: 'success',
    [InvoicePaymentStatus.UNPAID]: 'danger',
    [InvoicePaymentStatus.REFUND]: 'warning'
  }

  statusBadgeColors= {
    [InvoiceStatus.SENT]: 'primary',
    [InvoiceStatus.CANCELLED]: 'danger',
    [InvoiceStatus.UNCONFIRMED]: 'success',
    [InvoiceStatus.CONFIRMED]: 'primary'
  }

  protected readonly InvoicePaymentStatus = InvoicePaymentStatus;
  protected translateService = inject(TranslateService);

  constructor() {
    super();
    this.fields = this.service.fields.map(
      (field) => {
        return {
          ...field,
          filter: false,
        };
      }
    ).filter(field => field.fieldName != 'details');
  }

  getAll(params: any): Observable<PageResult<any>> {
    params.apartmentId = this.apartmentId;
    params.period = this.period;
    return super.getAll(params);
  }

  onView(data) {
    this.service.getOne(data.id).subscribe(
      (data) => {
        this.modalService.open({
          component: InvoiceCardComponent,
          title: [this.translateService.instant('VIEW')].concat(
            this.service.titleSingular ? [
              this.translateService.instant(this.service.titleSingular)
            ] : []
          ).join(' '),
          modalOptions: {size: 'xl'},
          componentOptions: {
            invoice: data,
            service: this.service
          }
        })
      }
    )
  }

  onPay(invoice: Invoice) {
    this.modalService.open({
      component: PaymentsFormComponent,
      title: 'PAYMENT.PAYMENT',
      componentOptions: {
        invoice,
        residentId: this.renter.id
      },
    }).then(r => {
      if (r === ModalClose.SUCCESS) {
        this.loadData()
      }
    })
  }

  createInvoice(apartmentId) {
    this.invoiceModalService.openInvoiceModal().then(res => {
      if (res?.confirmed) {
        const payload = {
          apartmentId,
          types: res?.selectedOptions // InvoiceOption[] → [0,1,2]
        };

        this.invoiceModalService.manualGenerateInvoice(apartmentId, res.selectedOptions).subscribe(
          {
            error: (error) => {
              this.sweetAlertService.error('', error.error.message).then((res) => {
                this.loadData();
              })
            },
            complete: () => {
              this.sweetAlertService.success('', "TOAST.TOAST_CREATE").then((res) => {
                this.loadData();
              })
            }
          }
      )
      }
    })
  }

  protected readonly invoicePaymentStatusMapper = invoicePaymentStatusMapper;
  protected readonly invoiceStatusMapper = invoiceStatusMapper;
}
